import { useState, useEffect, useCallback, useRef } from 'react';
import { squaresApi } from '../api';
import type { SquareWithNumbers, ClaimSquareInput, AssignSquareInput, LabelSquareInput } from '../types';

export function useSquares(boardId: string) {
  const [squares, setSquares] = useState<SquareWithNumbers[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const fetchSquares = useCallback(async () => {
    if (!boardId) return;
    // Only show loading spinner on initial load (stale-while-revalidate pattern)
    // This prevents the page from blinking when refetching data
    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const data = await squaresApi.getAll(boardId);
      setSquares(data);
      hasLoadedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch squares');
    } finally {
      setIsLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    fetchSquares();
  }, [fetchSquares]);

  const claimSquare = async (input: ClaimSquareInput) => {
    const square = await squaresApi.claim(boardId, input);
    await fetchSquares(); // Refresh all squares
    return square;
  };

  const assignSquare = async (input: AssignSquareInput) => {
    const square = await squaresApi.assign(boardId, input);
    await fetchSquares();
    return square;
  };

  const labelSquare = async (input: LabelSquareInput) => {
    const square = await squaresApi.label(boardId, input);
    await fetchSquares();
    return square;
  };

  const clearSquare = async (row: number, col: number) => {
    const square = await squaresApi.clear(boardId, row, col);
    await fetchSquares();
    return square;
  };

  // Convert to 2D grid for display
  const grid: SquareWithNumbers[][] = [];
  for (let row = 0; row < 10; row++) {
    grid[row] = [];
    for (let col = 0; col < 10; col++) {
      const square = squares.find((s) => s.row === row && s.col === col);
      if (square) {
        grid[row][col] = square;
      }
    }
  }

  return {
    squares,
    grid,
    isLoading,
    error,
    fetchSquares,
    refetch: fetchSquares,
    claimSquare,
    assignSquare,
    labelSquare,
    clearSquare,
  };
}
